import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { ERROR_CODES, logError, mapHttpStatusToError, getApiBaseUrl } from '../utils/errorCodes';

/**
 * ForgotPasswordPage component for the Agent Nexus
 *
 * This component provides a form for users to request a password reset link.
 * It sends a request to the backend which then sends an email with a reset link.
 */
const ForgotPasswordPage = () => {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  /**
   * Handle form submission
   * Sends password reset request to the backend
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use getApiBaseUrl for auth endpoints (removes /api suffix)
      const apiUrl = getApiBaseUrl(true);
      const response = await fetch(`${apiUrl}/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        logError({ code: 'NEXUS-0000', message: 'Password reset email requested', logLevel: 'info' }, {
          context: 'password_reset_request_success',
          email: email.replace(/(.{2}).*(@.*)/, '$1***$2') // Mask email for logging
        });
        setSuccess(true);
      } else {
        const errDef = mapHttpStatusToError(response.status, 'password_reset');
        const serverMessage = data.message || data.detail;
        logError(errDef, {
          context: 'password_reset_request_error',
          status: response.status,
          serverMessage
        });
        setError(serverMessage ? `${serverMessage} (${errDef.code})` : `${errDef.message} (${errDef.code})`);
      }
    } catch (err) {
      const errDef = err.message?.includes('fetch')
        ? ERROR_CODES.API_NETWORK_ERROR
        : ERROR_CODES.SYSTEM_UNEXPECTED;
      logError(errDef, {
        context: 'password_reset_request_exception',
        error: err.message
      });
      setError(`${errDef.message} (${errDef.code})`);
    } finally {
      setLoading(false);
    }
  };

  // Success state - show confirmation message
  if (success) {
    return (
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center">
            <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-[#FFC000]">
              <div className="bg-white dark:bg-gray-800 border-b border-[#FFC000] px-8 py-6">
                <h1 className="text-3xl font-bold text-[#0E3056] dark:text-white text-center">Check Your Email</h1>
              </div>
              <div className="p-8 text-center">
                <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
                  Please check your inbox and spam folder. The link will expire in 1 hour.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-[#0E3056] bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Back to Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-[#FFC000]">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-[#FFC000] px-8 py-6">
              <h1 className="text-3xl font-bold text-[#0E3056] dark:text-white text-center">Reset Password</h1>
            </div>

            {/* Form content */}
            <div className="p-8">
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Reset form */}
              <form className="space-y-4" onSubmit={handleSubmit}>
                {/* Email field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email address
                  </label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full pl-10 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                {/* Submit button */}
                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-[#0E3056] bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>
              </form>

              {/* Back to login link */}
              <div className="text-center mt-6">
                <Link
                  to="/login"
                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300"
                >
                  Back to Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ForgotPasswordPage;
